import { CostCenter } from '../../types';
import { normalizeCostCentersList } from '../../utils/textFormat';
import {
  parseSettingValue,
  rhAfastamentoDatesSettingSchema,
  rhCostCentersSettingSchema,
  rhRoleColorsSettingSchema,
  rhRolesSettingSchema,
} from '../rhDataSchemas';
import type { DbClient } from './types';

/**
 * Exige sessão autenticada antes de gravar settings (evita writes anônimos).
 * @param sb - Cliente API
 * @param context - Rótulo para mensagem de erro
 */
async function assertAuthenticatedSession(sb: DbClient, context: string): Promise<void> {
  const sessionResult = await sb.auth.getSession();
  const session = sessionResult?.data?.session;
  if (!session?.user) {
    throw new Error(`[${context}] Sessão não autenticada`);
  }
}

/**
 * Lê valor JSON de rh_settings (global ou por empresa).
 * @param sb - Cliente API
 * @param key - Chave do setting
 * @param companyId - UUID opcional; omitido = linha global (company_id null)
 */
export async function loadSetting<T>(
  sb: DbClient,
  key: string,
  companyId?: string,
): Promise<T | null> {
  const query = sb.from('rh_settings').select('value').eq('key', key);
  if (companyId) {
    query.eq('company_id', companyId);
  } else {
    query.is('company_id', null);
  }
  const { data } = await query.maybeSingle();
  return (data?.value as T | undefined) ?? null;
}

/**
 * Lê setting com validação Zod — descarta shape inválido e retorna null.
 * @param sb - Cliente API
 * @param key - Chave do setting
 * @param schema - Schema do valor JSON
 * @param companyId - UUID opcional
 */
export async function loadValidatedSetting<T>(
  sb: DbClient,
  key: string,
  schema: import('zod').ZodType<T>,
  companyId?: string,
): Promise<T | null> {
  const raw = await loadSetting<unknown>(sb, key, companyId);
  return parseSettingValue(schema, raw, key);
}

/**
 * Grava valor JSON em rh_settings (global ou por empresa).
 * @param sb - Cliente API
 * @param key - Chave do setting
 * @param value - Valor serializável
 * @param companyId - UUID opcional; omitido = linha global
 */
export async function saveSetting(
  sb: DbClient,
  key: string,
  value: unknown,
  companyId?: string,
): Promise<void> {
  await assertAuthenticatedSession(sb, 'saveSetting');
  const safeValue = value !== undefined && value !== null ? value : {};

  if (companyId) {
    const result = await sb.from('rh_settings').upsert(
      { company_id: companyId, key, value: safeValue },
      { onConflict: 'company_id,key' },
    );
    if (result.error) throw result.error;
    return;
  }

  const upd = await sb
    .from('rh_settings')
    .update({ value: safeValue })
    .eq('key', key)
    .is('company_id', null);
  if (upd.error) throw upd.error;

  const check = await sb
    .from('rh_settings')
    .select('id')
    .eq('key', key)
    .is('company_id', null)
    .maybeSingle();
  if (check.error) throw check.error;
  if (!check.data) {
    const ins = await sb.from('rh_settings').insert({ company_id: null, key, value: safeValue });
    if (ins.error) throw ins.error;
  }
}

/**
 * Carrega funções (roles) disponíveis para a empresa.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 */
export async function loadRoles(sb: DbClient, companyId: string): Promise<string[] | null> {
  const { data } = await sb
    .from('rh_settings')
    .select('value')
    .eq('company_id', companyId)
    .eq('key', 'available_roles')
    .maybeSingle();
  if (!data) return null;
  return parseSettingValue(rhRolesSettingSchema, data.value, 'available_roles');
}

/**
 * Persiste lista de funções da empresa.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 * @param roles - Nomes das funções
 */
export async function saveRoles(sb: DbClient, companyId: string, roles: string[]): Promise<void> {
  await assertAuthenticatedSession(sb, 'saveRoles');
  await sb.from('rh_settings').upsert(
    { company_id: companyId, key: 'available_roles', value: roles },
    { onConflict: 'company_id,key' },
  );
}

/**
 * Carrega mapa cor por função.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 */
export async function loadRoleColors(sb: DbClient, companyId: string): Promise<Record<string, string>> {
  const { data } = await sb
    .from('rh_settings')
    .select('value')
    .eq('company_id', companyId)
    .eq('key', 'role_colors')
    .maybeSingle();
  if (!data) return {};
  return parseSettingValue(rhRoleColorsSettingSchema, data.value, 'role_colors') ?? {};
}

/**
 * Persiste cores por função.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 * @param roleColors - Mapa função → cor hex
 */
export async function saveRoleColors(
  sb: DbClient,
  companyId: string,
  roleColors: Record<string, string>,
): Promise<void> {
  await assertAuthenticatedSession(sb, 'saveRoleColors');
  await sb.from('rh_settings').upsert(
    { company_id: companyId, key: 'role_colors', value: roleColors },
    { onConflict: 'company_id,key' },
  );
}

/**
 * Centros de custo compartilhados (global) com fallback legado por empresa.
 * @param sb - Cliente API
 * @param companyIdForLegacyFallback - UUID para ler legado se global ausente
 */
export async function loadSharedCostCenters(
  sb: DbClient,
  companyIdForLegacyFallback: string,
): Promise<CostCenter[]> {
  const globalRaw = await loadValidatedSetting(sb, 'cost_centers', rhCostCentersSettingSchema);
  if (globalRaw != null && globalRaw.length > 0) {
    return normalizeCostCentersList(globalRaw);
  }
  const legacy = await loadValidatedSetting(
    sb,
    'cost_centers',
    rhCostCentersSettingSchema,
    companyIdForLegacyFallback,
  );
  return normalizeCostCentersList(legacy ?? []);
}
